import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../../context/NotificationContext';
import axios from 'axios';
import { API_URL } from '../../../config/api';
import CancelOrderModal from '../../../components/CancelOrderModal';
import { playNewOrderSound } from '../../../utils/notificationSound';
import '../main-admin/Orders.css';

const ORDERS_POLL_INTERVAL_MS = 30000;

const Orders = () => {
  const { t } = useTranslation();
  const { showError, showSuccess } = useNotification();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const previousOrderIdsRef = useRef(new Set());

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/store-owner/orders`);
      const newOrders = response.data || [];
      const newIds = new Set(newOrders.map((o) => o._id));
      if (previousOrderIdsRef.current.size > 0 && document.visibilityState === 'visible') {
        const hasNew = newOrders.some((o) => !previousOrderIdsRef.current.has(o._id));
        if (hasNew) playNewOrderSound();
      }
      previousOrderIdsRef.current = newIds;
      setOrders(newOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, ORDERS_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const handleView = async (orderId) => {
    try {
      const response = await axios.get(`${API_URL}/store-owner/orders/${orderId}`);
      setSelectedOrder(response.data);
    } catch (error) {
      console.error('Error fetching order details:', error);
      showError(t('admin.loading') + ' ' + t('admin.orderDetails'));
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedOrder || updatingStatus) return;
    if (newStatus === 'cancelled') {
      setShowCancelModal(true);
      return;
    }
    setUpdatingStatus(true);
    try {
      await axios.patch(`${API_URL}/store-owner/orders/${selectedOrder._id}/status`, {
        status: newStatus
      });
      const updatedOrder = { ...selectedOrder, status: newStatus };
      setSelectedOrder(updatedOrder);
      setOrders(orders.map(order =>
        order._id === selectedOrder._id ? updatedOrder : order
      ));
      showSuccess(t('admin.statusUpdated'));
    } catch (error) {
      console.error('Error updating order status:', error);
      showError(error.response?.data?.message || t('admin.statusUpdateFailed'));
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleCancelOrderConfirm = async (cancellationMessage) => {
    if (!selectedOrder || updatingStatus) return;
    setUpdatingStatus(true);
    try {
      const res = await axios.patch(`${API_URL}/store-owner/orders/${selectedOrder._id}/status`, {
        status: 'cancelled',
        cancellationMessage
      });
      const updatedOrder = res.data;
      setSelectedOrder(updatedOrder);
      setOrders(orders.map(order =>
        order._id === selectedOrder._id ? updatedOrder : order
      ));
      setShowCancelModal(false);
      showSuccess(t('admin.statusUpdated'));
    } catch (error) {
      console.error('Error cancelling order:', error);
      showError(error.response?.data?.message || t('admin.statusUpdateFailed'));
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getPaymentStatusBadge = (status) => {
    const statusColors = {
      paid: { bg: '#27ae60', color: 'white' },
      pending: { bg: '#f39c12', color: 'white' },
      failed: { bg: '#e74c3c', color: 'white' },
      refunded: { bg: '#95a5a6', color: 'white' }
    };
    const colors = statusColors[status] || { bg: '#95a5a6', color: 'white' };
    return (
      <span 
        className="payment-status-badge" 
        style={{ 
          backgroundColor: colors.bg, 
          color: colors.color,
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          textTransform: 'capitalize'
        }}
      >
        {status || 'pending'}
      </span>
    );
  };

  const getOrderStatusBadge = (status) => {
    const statusColors = {
      pending: { bg: '#3498db', color: 'white' },
      confirmed: { bg: '#3498db', color: 'white' },
      preparing: { bg: '#9b59b6', color: 'white' },
      readyForPickup: { bg: '#e67e22', color: 'white' },
      outForDelivery: { bg: '#e67e22', color: 'white' },
      delivered: { bg: '#27ae60', color: 'white' },
      cancelled: { bg: '#e74c3c', color: 'white' }
    };
    const colors = statusColors[status] || { bg: '#95a5a6', color: 'white' };
    return (
      <span 
        className="order-status-badge" 
        style={{ 
          backgroundColor: colors.bg, 
          color: colors.color,
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          textTransform: 'capitalize'
        }}
      >
        {status}
      </span>
    );
  };

  const filteredOrders = statusFilter === 'all' 
    ? orders 
    : orders.filter(order => order.status === statusFilter);

  if (loading) {
    return (
      <div className="orders-container">
        <div className="loading-spinner">{t('admin.loading')}</div>
      </div>
    );
  }

  return (
    <div className="orders-container">
      <div className="orders-header">
        <h2>{t('admin.store')} {t('admin.orders')}</h2>
        <div className="status-filter">
          <label>{t('admin.filterByStatus')}</label>
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('admin.all')} {t('admin.orders')}</option>
            <option value="pending">{t('admin.pending')}</option>
            <option value="confirmed">{t('admin.confirmed')}</option>
            <option value="preparing">{t('admin.preparing')}</option>
            <option value="readyForPickup">{t('admin.readyForPickup') || 'Ready for pickup'}</option>
            <option value="outForDelivery">{t('admin.outForDelivery')}</option>
            <option value="delivered">{t('admin.delivered')}</option>
            <option value="cancelled">{t('admin.cancelled')}</option>
          </select>
        </div>
      </div>

      <div className="orders-table-wrapper">
        <table className="orders-table">
          <thead>
            <tr>
              <th>{t('admin.orderNumber')}</th>
              <th>{t('admin.customer')}</th>
              <th>{t('admin.store')}</th>
              <th>{t('admin.paymentStatus')}</th>
              <th>{t('admin.total')}</th>
              <th>{t('admin.status')}</th>
              <th>{t('admin.date')}</th>
              <th>{t('admin.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-orders">
                  {t('admin.noOrders')}
                </td>
              </tr>
            ) : (
              filteredOrders.map(order => (
                <tr key={order._id}>
                  <td className="order-number">{order.orderNumber || order._id.slice(-6)}</td>
                  <td>
                    <div className="customer-info">
                      <div className="customer-name">
                        {order.user ? `${order.user.firstName || ''} ${order.user.lastName || ''}`.trim() : (order.customerName || '—')}
                      </div>
                      <div className="customer-email">{order.user?.email ?? order.customerEmail ?? '—'}</div>
                    </div>
                  </td>
                  <td className="restaurant-name">{order.restaurantName || 'N/A'}</td>
                  <td>{getPaymentStatusBadge(order.paymentStatus)}</td>
                  <td className="order-total">€{order.total?.toFixed(2)}</td>
                  <td>{getOrderStatusBadge(order.status)}</td>
                  <td className="order-date">
                    {new Date(order.createdAt).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </td>
                  <td>
                    <button 
                      onClick={() => handleView(order._id)} 
                      className="view-btn"
                    >
                      {t('admin.view')}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selectedOrder && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{t('admin.orderDetails')} - {selectedOrder.orderNumber || selectedOrder._id.slice(-6)}</h2>
              <button 
                className="close-btn" 
                onClick={() => setSelectedOrder(null)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="detail-section">
                <h3>{t('admin.customer')} {t('admin.basicInformation')}</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Name:</label>
                    <span>{selectedOrder.user?.firstName} {selectedOrder.user?.lastName}</span>
                  </div>
                  <div className="detail-item">
                    <label>Email:</label>
                    <span>{selectedOrder.user?.email}</span>
                  </div>
                  <div className="detail-item">
                    <label>Phone:</label>
                    <span>{selectedOrder.user?.phoneNumber || 'N/A'}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>{t('admin.orderDetails')}</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>{t('admin.store')}:</label>
                    <span>{selectedOrder.restaurantName || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('admin.orderStatus')}:</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {getOrderStatusBadge(selectedOrder.status)}
                      <select
                        value={selectedOrder.status}
                        onChange={(e) => handleStatusChange(e.target.value)}
                        disabled={updatingStatus}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: '1px solid #ddd',
                          fontSize: '13px',
                          cursor: updatingStatus ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <option value="confirmed">{t('admin.confirmed')}</option>
                        <option value="preparing">{t('admin.preparing')}</option>
                        <option value="readyForPickup">{t('admin.readyForPickup') || 'Ready for pickup'}</option>
                        <option value="cancelled">{t('admin.cancelled')}</option>
                      </select>
                    </div>
                  </div>
                  <div className="detail-item">
                    <label>{t('admin.paymentStatus')}:</label>
                    <span>{getPaymentStatusBadge(selectedOrder.paymentStatus)}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('admin.paymentMethod')}:</label>
                    <span className="payment-method">{selectedOrder.paymentMethod || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('admin.orderDate')}:</label>
                    <span>{new Date(selectedOrder.createdAt).toLocaleString('en-GB')}</span>
                  </div>
                  {selectedOrder.deliveryDriver && (
                    <div className="detail-item">
                      <label>{t('admin.driver')}:</label>
                      <span>
                        {selectedOrder.deliveryDriver?.firstName} {selectedOrder.deliveryDriver?.lastName}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {selectedOrder.items && selectedOrder.items.length > 0 && (
                <div className="detail-section">
                  <h3>{t('admin.items')}</h3>
                  <div className="items-list">
                    {selectedOrder.items.map((item, index) => (
                      <div key={index} className="item-row">
                        <div className="item-info">
                          <span className="item-name">{item.name}</span>
                          {item.selectedVariations && (
                            <div className="item-variations">
                              {Array.isArray(item.selectedVariations) ? (
                                item.selectedVariations.map((variation, vIdx) => (
                                  <div key={vIdx} className="variation-group">
                                    <span className="variation-name">{variation.name}:</span>
                                    <div className="variation-options">
                                      {variation.selectedOptions?.map((option, oIdx) => (
                                        <span key={oIdx} className="variation-option-badge">{option}</span>
                                      ))}
                                    </div>
                                  </div>
                                ))
                              ) : (
                                Object.entries(item.selectedVariations).map(([variationName, selectedOptions], vIdx) => (
                                  <div key={vIdx} className="variation-group">
                                    <span className="variation-name">{variationName}:</span>
                                    <div className="variation-options">
                                      {Array.isArray(selectedOptions) ? (
                                        selectedOptions.map((option, oIdx) => (
                                          <span key={oIdx} className="variation-option-badge">{option}</span>
                                        ))
                                      ) : (
                                        <span className="variation-option-badge">{selectedOptions}</span>
                                      )}
                                    </div>
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                          {((item.ingredients && item.ingredients.length > 0) || (item.selectedIngredients && item.selectedIngredients.length > 0)) && (
                            <div className="item-ingredients">
                              {item.selectedIngredients && item.selectedIngredients.length > 0 && (
                                <div className="item-ingredients-included">
                                  <span className="ingredients-label">{t('admin.ingredientsIncluded') || 'Ingredients (included)'}:</span>
                                  <span>{item.selectedIngredients.join(', ')}</span>
                                </div>
                              )}
                              {item.ingredients && item.ingredients.length > 0 && (() => {
                                const selected = item.selectedIngredients || [];
                                const removed = item.ingredients.filter((ing) => !selected.includes(ing));
                                return removed.length > 0 ? (
                                  <div className="item-ingredients-removed">
                                    <span className="ingredients-label">{t('admin.ingredientsRemoved') || 'Without'}:</span>
                                    <span className="ingredients-removed-list">{removed.join(', ')}</span>
                                  </div>
                                ) : null;
                              })()}
                            </div>
                          )}
                        </div>
                        <div className="item-quantity">{t('admin.quantity')}: {item.quantity}</div>
                        <div className="item-price">€{item.price?.toFixed(2)}</div>
                        <div className="item-total">€{(item.price * item.quantity)?.toFixed(2)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="detail-section">
                <h3>{t('admin.total')}</h3>
                <div className="pricing-breakdown">
                  <div className="pricing-row">
                    <span>{t('admin.subtotal')}:</span>
                    <span>€{selectedOrder.subtotalBeforeTax?.toFixed(2) || '0.00'}</span>
                  </div>
                  {selectedOrder.taxAmount > 0 && (
                    <div className="pricing-row">
                      <span>{t('admin.tax')} ({selectedOrder.subtotalBeforeTax > 0 
                        ? ((selectedOrder.taxAmount / selectedOrder.subtotalBeforeTax) * 100).toFixed(1)
                        : '0.0'}%):</span>
                      <span>€{selectedOrder.taxAmount?.toFixed(2)}</span>
                    </div>
                  )}
                  {selectedOrder.deliveryFee > 0 && (
                    <div className="pricing-row">
                      <span>{t('admin.deliveryFee')}:</span>
                      <span>€{selectedOrder.deliveryFee?.toFixed(2)}</span>
                    </div>
                  )}
                  {selectedOrder.serviceFee !== undefined && selectedOrder.serviceFee !== null && selectedOrder.serviceFee > 0 && (
                    <div className="pricing-row">
                      <span>{t('admin.serviceFee')} {selectedOrder.serviceFeeRate ? `(${selectedOrder.serviceFeeRate.toFixed(2)}%)` : ''}:</span>
                      <span>€{selectedOrder.serviceFee?.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="pricing-row total-row">
                    <span>{t('admin.total')}:</span>
                    <span>€{selectedOrder.total?.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {selectedOrder.deliveryAddress && (
                <div className="detail-section">
                  <h3>{t('admin.deliveryAddress')}</h3>
                  <div className="address-info">
                    <p><strong>{selectedOrder.deliveryAddress.label || t('admin.deliveryAddress')}</strong></p>
                    <p>{selectedOrder.deliveryAddress.street}</p>
                    <p>{selectedOrder.deliveryAddress.city} {selectedOrder.deliveryAddress.zipCode}{selectedOrder.deliveryAddress.addressDetails ? ` — ${selectedOrder.deliveryAddress.addressDetails}` : ''}</p>
                  </div>
                </div>
              )}

              {(selectedOrder.restaurantInstructions || selectedOrder.driverInstructions || selectedOrder.customerInstructions) && (
                <div className="detail-section">
                  <h3>{t('admin.customerInstructionsSection')}</h3>
                  <div className="detail-grid">
                    {selectedOrder.restaurantInstructions && (
                      <div className="detail-item detail-item-full">
                        <label>{t('admin.restaurantInstructions')}:</label>
                        <span className="instructions-text">{selectedOrder.restaurantInstructions}</span>
                      </div>
                    )}
                    {selectedOrder.driverInstructions && (
                      <div className="detail-item detail-item-full">
                        <label>{t('admin.driverInstructions')}:</label>
                        <span className="instructions-text">{selectedOrder.driverInstructions}</span>
                      </div>
                    )}
                    {selectedOrder.customerInstructions && !selectedOrder.restaurantInstructions && !selectedOrder.driverInstructions && (
                      <div className="detail-item detail-item-full">
                        <label>{t('admin.customerInstructionsSection')}:</label>
                        <span className="instructions-text">{selectedOrder.customerInstructions}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button 
                className="close-modal-btn" 
                onClick={() => setSelectedOrder(null)}
              >
                {t('admin.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      <CancelOrderModal
        show={showCancelModal}
        orderNumber={selectedOrder?.orderNumber}
        onConfirm={handleCancelOrderConfirm}
        onClose={() => setShowCancelModal(false)}
      />
    </div>
  );
};

export default Orders;
